import { DeckManager, type DeckSetupOptions } from "./partyMode/decks";
import { BeeDiceManager } from "./partyMode/beeDice";
import { Random } from "./partyMode/random";
import { RoundManager } from "./partyMode/roundManager";
import { Timer } from "./partyMode/timer";
import { decodeSelectedActionCards, encodeSelectedActionCards, getActionCardDefinition, getCellarDraftOfferPool, type ActionCardDefinition } from "./partyMode/cardCatalog";
import { isCellarCampaignSeed, isHiveInfluencerSeed } from "./partyMode/seedEvents";
import { toCanvas } from "qrcode";
import { mountHeaderBrandLogos } from "../../Shared/brandLogos";

export const timer = new Timer();
export const deckManager = new DeckManager();
export const beeDiceManager = new BeeDiceManager();
export const roundManager = new RoundManager();

export let rng = new Random(null);
export let urlPath = '';

type CampaignPhase = "opening-draft" | "in-game" | "carryover-draft" | "between-games" | "complete";
type CampaignFilter = "all" | "season-1" | "season-2" | "season-3" | "base" | "advanced" | "cellar";

interface CellarCampaignState {
    gameNumber: 1 | 2 | 3;
    phase: CampaignPhase;
    currentGuaranteedNames: string[];
    openingChosenNames: string[];
    openingCurrentOfferNames: string[];
    openingCurrentPickName: string | null;
    carryoverChosenNames: string[];
    carryoverAvailableNames: string[];
    carryoverCurrentPickName: string | null;
    draftFilter: CampaignFilter;
}

let hiveExpansionEnabled = false;
let cellarExpansionEnabled = false;
let advancedCardsEnabled = false;
let campaignState: CellarCampaignState | null = null;
let isApplyingState = false;

let seedInputElement: HTMLInputElement | null = null;
let seedQrCodeElement: HTMLCanvasElement | null = null;
let hiveToggleElement: HTMLButtonElement | null = null;
let cellarToggleElement: HTMLButtonElement | null = null;
let advancedCardsToggleElement: HTMLButtonElement | null = null;
let cellarGameDisplayElement: HTMLElement | null = null;
let cellarGameLabelElement: HTMLElement | null = null;
let cellarOverlayElement: HTMLElement | null = null;
let cellarEyebrowElement: HTMLElement | null = null;
let cellarTitleElement: HTMLElement | null = null;
let cellarDescriptionElement: HTMLElement | null = null;
let cellarSummaryElement: HTMLElement | null = null;
let cellarFiltersElement: HTMLElement | null = null;
let cellarCardGridElement: HTMLElement | null = null;
let cellarNextPlayerButton: HTMLButtonElement | null = null;
let cellarDoneButton: HTMLButtonElement | null = null;
let cellarNextGameButton: HTMLButtonElement | null = null;
let cellarCloseButton: HTMLButtonElement | null = null;

const tabletViewportQuery = window.matchMedia('(min-width: 769px) and (max-width: 1366px) and (pointer: coarse)');
const cellarOfferPool = getCellarDraftOfferPool();
roundManager.setDeckSetupOptionsProvider((): DeckSetupOptions => ({
    hiveExpansionEnabled,
    cellarExpansionEnabled,
    includeAdvancedCards: advancedCardsEnabled,
    guaranteedActionCardNames: cellarExpansionEnabled ? getCurrentGuaranteedCardNames() : [],
    currentSeed: rng.getCurrentSeed().toString(),
    campaignGameNumber: campaignState?.gameNumber ?? 1
}));
roundManager.setStateChangeHandler(() => {
    if (isApplyingState) {
        return;
    }

    beeDiceManager.setEnabled(hiveExpansionEnabled, roundManager.getCurrentRound());
    syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
});
roundManager.setGameFinishedHandler(() => {
    if (isApplyingState) {
        return;
    }

    handleGameFinished();
});

syncTabletViewportHeight();
window.addEventListener('resize', syncTabletViewportHeight);
window.addEventListener('orientationchange', syncTabletViewportHeight);
if (typeof tabletViewportQuery.addEventListener === 'function') {
    tabletViewportQuery.addEventListener('change', syncTabletViewportHeight);
} else {
    tabletViewportQuery.addListener(syncTabletViewportHeight);
}
window.visualViewport?.addEventListener('resize', syncTabletViewportHeight);
window.visualViewport?.addEventListener('scroll', syncTabletViewportHeight);

document.addEventListener('DOMContentLoaded', () => {
    mountHeaderBrandLogos();
    beeDiceManager.initialize();

    seedInputElement = document.getElementById('seed-input') as HTMLInputElement | null;
    seedQrCodeElement = document.getElementById('seed-qr-code') as HTMLCanvasElement | null;
    hiveToggleElement = document.getElementById('hive-toggle') as HTMLButtonElement | null;
    cellarToggleElement = document.getElementById('cellar-toggle') as HTMLButtonElement | null;
    advancedCardsToggleElement = document.getElementById('advanced-cards-toggle') as HTMLButtonElement | null;
    cellarGameDisplayElement = document.getElementById('cellar-game-display');
    cellarGameLabelElement = document.getElementById('cellar-game-label');
    cellarOverlayElement = document.getElementById('cellar-campaign-overlay');
    cellarEyebrowElement = document.getElementById('cellar-campaign-eyebrow');
    cellarTitleElement = document.getElementById('cellar-campaign-title');
    cellarDescriptionElement = document.getElementById('cellar-campaign-description');
    cellarSummaryElement = document.getElementById('cellar-campaign-summary');
    cellarFiltersElement = document.getElementById('cellar-campaign-filters');
    cellarCardGridElement = document.getElementById('cellar-campaign-card-grid');
    cellarNextPlayerButton = document.getElementById('cellar-next-player-btn') as HTMLButtonElement | null;
    cellarDoneButton = document.getElementById('cellar-done-btn') as HTMLButtonElement | null;
    cellarNextGameButton = document.getElementById('cellar-next-game-btn') as HTMLButtonElement | null;
    cellarCloseButton = document.getElementById('cellar-close-btn') as HTMLButtonElement | null;

    initializeSeedAndToggles();
    setupSeedControls();
    setupAppMenu();
    setupCardRowToggles();
    setupCellarCampaignControls();
    startFlowForCurrentSettings(true);
});

function initializeSeedAndToggles(): void {
    const extraPath = window.location.pathname;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const seedFromQuery = urlSearchParams.get('seed');
    const extraPathClean = extraPath.replace("/", "");
    const hasLegacySeedRoute = (seedFromQuery != null && seedFromQuery.trim() !== '') || extraPathClean !== '';

    urlPath = extraPathClean;

    const expansions = parseExpansionParam(urlSearchParams);
    hiveExpansionEnabled = expansions.includes('hive');
    cellarExpansionEnabled = expansions.includes('cellar');
    advancedCardsEnabled = parseAdvancedCardsParam(urlSearchParams, hasLegacySeedRoute);

    if (seedFromQuery != null && seedFromQuery.trim() !== '') {
        rng = new Random(seedFromQuery);
    } else if (urlPath !== '') {
        rng = new Random(urlPath);
    }

    applySeedExpansionRequirements();

    if (cellarExpansionEnabled) {
        campaignState = createCampaignStateFromUrl(urlSearchParams);
    }

    seedInputElement!.value = rng.getCurrentSeed().toString();
    beeDiceManager.setSeed(seedInputElement!.value);
}

function setupSeedControls(): void {
    const useSeedBtn = document.getElementById('use-seed-btn');
    const randomSeedBtn = document.getElementById('random-seed-btn') as HTMLButtonElement | null;

    if (seedInputElement == null || useSeedBtn == null) {
        return;
    }

    seedInputElement.value = rng.getCurrentSeed().toString();

    const applySeed = (seed: string) => {
        const enteredSeed = seed.trim();

        if (!enteredSeed) {
            console.error('No seed entered');
            return;
        }

        seedInputElement!.value = enteredSeed;
        rng = new Random(enteredSeed);
        beeDiceManager.setSeed(rng.getCurrentSeed().toString());
        applySeedExpansionRequirements();
        campaignState = cellarExpansionEnabled ? createInitialCampaignState() : null;
        startFlowForCurrentSettings(true);
    };

    useSeedBtn.addEventListener('click', () => {
        applySeed(seedInputElement!.value);
    });

    randomSeedBtn?.addEventListener('click', () => {
        applySeed(Random.generateSeedName());
    });
}

function setupAppMenu() {
    const menuCell = document.querySelector<HTMLElement>('.menu-cell');
    const menuButton = document.getElementById('menu-button') as HTMLButtonElement | null;
    const appMenu = document.getElementById('app-menu');
    const timerToggle = document.getElementById('timer-toggle') as HTMLButtonElement | null;

    if (menuCell == null || menuButton == null || appMenu == null) {
        return;
    }

    const setSwitchState = (toggle: HTMLButtonElement | null, isOn: boolean) => {
        if (toggle == null) {
            return;
        }

        toggle.classList.toggle('is-on', isOn);
        toggle.setAttribute('aria-checked', isOn.toString());
    };

    const setMenuOpen = (isOpen: boolean) => {
        menuCell.classList.toggle('is-open', isOpen);
        menuButton.setAttribute('aria-expanded', isOpen.toString());
        menuButton.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
        appMenu.setAttribute('aria-hidden', (!isOpen).toString());
    };

    menuButton.addEventListener('click', (event) => {
        event.stopPropagation();
        setMenuOpen(!menuCell.classList.contains('is-open'));
    });

    appMenu.addEventListener('click', (event) => {
        event.stopPropagation();
    });

    document.addEventListener('click', () => {
        setMenuOpen(false);
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            setMenuOpen(false);
        }
    });

    setSwitchState(timerToggle, roundManager.isTimerEnabled());
    setSwitchState(hiveToggleElement, hiveExpansionEnabled);
    setSwitchState(cellarToggleElement, cellarExpansionEnabled);
    setSwitchState(advancedCardsToggleElement, advancedCardsEnabled);

    timerToggle?.addEventListener('click', () => {
        const isOn = !timerToggle.classList.contains('is-on');
        roundManager.setTimerEnabled(isOn);
        setSwitchState(timerToggle, roundManager.isTimerEnabled());
        syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
    });

    hiveToggleElement?.addEventListener('click', () => {
        hiveExpansionEnabled = !hiveExpansionEnabled;

        if (hiveExpansionEnabled) {
            cellarExpansionEnabled = false;
            campaignState = null;
        }

        setSwitchState(hiveToggleElement, hiveExpansionEnabled);
        setSwitchState(cellarToggleElement, cellarExpansionEnabled);
        startFlowForCurrentSettings(true);
    });

    cellarToggleElement?.addEventListener('click', () => {
        cellarExpansionEnabled = !cellarExpansionEnabled;

        if (cellarExpansionEnabled) {
            hiveExpansionEnabled = false;
            campaignState = createInitialCampaignState();
        } else {
            campaignState = null;
            hideCellarCampaignOverlay();
        }

        setSwitchState(cellarToggleElement, cellarExpansionEnabled);
        setSwitchState(hiveToggleElement, hiveExpansionEnabled);
        startFlowForCurrentSettings(true);
    });

    advancedCardsToggleElement?.addEventListener('click', () => {
        advancedCardsEnabled = !advancedCardsEnabled;
        setSwitchState(advancedCardsToggleElement, advancedCardsEnabled);
        startFlowForCurrentSettings(true);
    });
}

function setupCardRowToggles() {
    const mobileLayoutQuery = window.matchMedia('(max-width: 768px)');
    const toggles = Array.from(document.querySelectorAll<HTMLButtonElement>('.row-toggle'));
    const cardRows = Array.from(document.querySelectorAll<HTMLElement>('.card-row'));
    const cardContainer = document.querySelector<HTMLElement>('.card-container');
    const topRows = document.querySelector<HTMLElement>('.top-rows');
    const bottomRow = document.querySelector<HTMLElement>('.bottom-row');

    const areRowsCollapsed = (container: HTMLElement | null) => {
        const rows = Array.from(container?.querySelectorAll<HTMLElement>('.card-row:not(.is-hidden)') ?? []);

        return rows.length > 0 && rows.every((row) => row.classList.contains('is-collapsed'));
    };

    const updateLayoutState = () => {
        const topSectionsCollapsed = areRowsCollapsed(topRows);
        const bottomSectionCollapsed = areRowsCollapsed(bottomRow);

        topRows?.classList.toggle('is-fully-collapsed', topSectionsCollapsed);
        bottomRow?.classList.toggle('is-fully-collapsed', bottomSectionCollapsed);
        cardContainer?.classList.toggle('top-sections-collapsed', topSectionsCollapsed);
        cardContainer?.classList.toggle('has-collapsed-rows', cardRows.some((row) => !row.classList.contains('is-hidden') && row.classList.contains('is-collapsed')));
    };

    const setCollapsed = (toggle: HTMLButtonElement, cardRow: HTMLElement, sectionName: string, isCollapsed: boolean) => {
        cardRow.classList.toggle('is-collapsed', isCollapsed);
        toggle.textContent = isCollapsed ? '+' : '-';
        toggle.setAttribute('aria-expanded', (!isCollapsed).toString());
        toggle.setAttribute('aria-label', `${isCollapsed ? 'Show' : 'Minimize'} ${sectionName}`);
        updateLayoutState();
    };

    const syncToggleAvailability = () => {
        toggles.forEach((toggle) => {
            const cardRow = toggle.closest<HTMLElement>('.card-row');
            const sectionName = toggle.dataset.sectionName ?? 'section';

            toggle.disabled = !mobileLayoutQuery.matches;

            if (cardRow != null && !mobileLayoutQuery.matches) {
                setCollapsed(toggle, cardRow, sectionName, false);
            }
        });
    };

    toggles.forEach((toggle) => {
        const cardRow = toggle.closest<HTMLElement>('.card-row');
        const sectionName = toggle.dataset.sectionName ?? 'section';

        if (cardRow == null) {
            return;
        }

        setCollapsed(toggle, cardRow, sectionName, false);

        toggle.addEventListener('click', (event) => {
            event.stopPropagation();

            if (!mobileLayoutQuery.matches) {
                setCollapsed(toggle, cardRow, sectionName, false);
                return;
            }

            setCollapsed(toggle, cardRow, sectionName, !cardRow.classList.contains('is-collapsed'));
        });
    });

    syncToggleAvailability();
    window.addEventListener('resize', syncToggleAvailability);
    window.addEventListener('orientationchange', syncToggleAvailability);
    window.addEventListener('card-row-visibility-change', updateLayoutState);
}

function setupCellarCampaignControls(): void {
    cellarNextPlayerButton?.addEventListener('click', () => {
        if (campaignState == null) {
            return;
        }

        if (campaignState.phase === "opening-draft") {
            finalizeOpeningDraftPick();
            campaignState.openingCurrentOfferNames = generateCellarOffer(campaignState.openingChosenNames);
            renderCellarCampaignOverlay();
            syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
            return;
        }

        if (campaignState.phase === "carryover-draft") {
            finalizeCarryoverDraftPick();
            renderCellarCampaignOverlay();
            syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
        }
    });

    cellarDoneButton?.addEventListener('click', () => {
        if (campaignState == null) {
            return;
        }

        if (campaignState.phase === "opening-draft") {
            if (campaignState.openingCurrentPickName != null || campaignState.openingChosenNames.length === 0) {
                return;
            }

            campaignState.currentGuaranteedNames = campaignState.openingChosenNames.slice();
            campaignState.phase = "in-game";
            campaignState.openingCurrentOfferNames = [];
            startFlowForCurrentSettings(true);
            return;
        }

        if (campaignState.phase === "carryover-draft") {
            if (campaignState.carryoverCurrentPickName != null || campaignState.carryoverChosenNames.length === 0) {
                return;
            }

            campaignState.gameNumber = Math.min(3, campaignState.gameNumber + 1) as 1 | 2 | 3;
            campaignState.currentGuaranteedNames = campaignState.carryoverChosenNames.slice();
            campaignState.carryoverChosenNames = [];
            campaignState.carryoverAvailableNames = [];
            campaignState.carryoverCurrentPickName = null;
            campaignState.draftFilter = "all";
            campaignState.phase = "between-games";
            renderCellarCampaignOverlay();
            syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
        }
    });

    cellarNextGameButton?.addEventListener('click', () => {
        if (campaignState == null || campaignState.phase !== "between-games") {
            return;
        }

        campaignState.phase = "in-game";
        startFlowForCurrentSettings(true);
    });

    cellarCloseButton?.addEventListener('click', () => {
        if (campaignState?.phase === "complete") {
            hideCellarCampaignOverlay();
        }
    });
}

function createInitialCampaignState(): CellarCampaignState {
    return {
        gameNumber: 1,
        phase: "opening-draft",
        currentGuaranteedNames: [],
        openingChosenNames: [],
        openingCurrentOfferNames: generateCellarOffer([]),
        openingCurrentPickName: null,
        carryoverChosenNames: [],
        carryoverAvailableNames: [],
        carryoverCurrentPickName: null,
        draftFilter: "all"
    };
}

function createCampaignStateFromUrl(searchParams: URLSearchParams): CellarCampaignState {
    const rawGameNumber = Number.parseInt(searchParams.get('cg') ?? '1', 10);
    const gameNumber = rawGameNumber === 2 || rawGameNumber === 3 ? rawGameNumber : 1;
    const guaranteedNames = decodeSelectedActionCards(searchParams.get('cc') ?? '');

    return {
        gameNumber,
        phase: guaranteedNames.length > 0 || gameNumber > 1 ? "in-game" : "opening-draft",
        currentGuaranteedNames: guaranteedNames.slice(),
        openingChosenNames: gameNumber === 1 ? guaranteedNames.slice() : [],
        openingCurrentOfferNames: gameNumber === 1 && guaranteedNames.length === 0 ? generateCellarOffer([]) : [],
        openingCurrentPickName: null,
        carryoverChosenNames: [],
        carryoverAvailableNames: [],
        carryoverCurrentPickName: null,
        draftFilter: "all"
    };
}

function applySeedExpansionRequirements(): void {
    const requiresCellar = isCellarCampaignSeed(rng.getCurrentSeed());
    const requiresHive = isHiveInfluencerSeed(rng.getCurrentSeed());
    if (requiresCellar) {
        cellarExpansionEnabled = true;
        hiveExpansionEnabled = false;
    }
    if (requiresHive) {
        hiveExpansionEnabled = true;
        cellarExpansionEnabled = false;
        campaignState = null;
    }
    // Event seeds select their required expansion; other seeds unlock the controls.
    if (cellarToggleElement != null) cellarToggleElement.disabled = requiresCellar || requiresHive;
    if (hiveToggleElement != null) hiveToggleElement.disabled = requiresCellar || requiresHive;
}

function startFlowForCurrentSettings(resetRoundManager: boolean): void {
    isApplyingState = true;
    applySeedExpansionRequirements();
    syncToggleVisualState();

    if (seedInputElement != null) {
        seedInputElement.value = rng.getCurrentSeed().toString();
    }

    beeDiceManager.setSeed(rng.getCurrentSeed().toString());
    syncCellarGameDisplay();

    if (cellarExpansionEnabled) {
        if (campaignState == null) {
            campaignState = createInitialCampaignState();
        }

        if (campaignState.phase === "opening-draft" || campaignState.phase === "carryover-draft" || campaignState.phase === "between-games" || campaignState.phase === "complete") {
            if (campaignState.phase === "opening-draft" && campaignState.openingCurrentOfferNames.length === 0) {
                campaignState.openingCurrentOfferNames = generateCellarOffer(campaignState.openingChosenNames);
            }

            renderCellarCampaignOverlay();
        } else {
            hideCellarCampaignOverlay();
        }
    } else {
        campaignState = null;
        hideCellarCampaignOverlay();
    }

    if (!cellarExpansionEnabled || campaignState?.phase === "in-game" || campaignState?.phase === "carryover-draft" || campaignState?.phase === "between-games" || campaignState?.phase === "complete") {
        setupBoardForCurrentGame(resetRoundManager);
    }

    isApplyingState = false;
    syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
}

function syncCellarGameDisplay(): void {
    if (cellarGameDisplayElement == null || cellarGameLabelElement == null) {
        return;
    }

    const showCellarGame = cellarExpansionEnabled && campaignState != null;
    cellarGameDisplayElement.classList.toggle('is-hidden', !showCellarGame);

    if (!showCellarGame) {
        return;
    }

    cellarGameLabelElement.textContent = `Cellar Game ${campaignState.gameNumber}`;
}

function setupBoardForCurrentGame(resetRoundManager: boolean): void {
    if (resetRoundManager) {
        roundManager.resetRounds();
        rng.restartGame();

        if (cellarExpansionEnabled && campaignState != null && campaignState.gameNumber > 1) {
            rng.advance((campaignState.gameNumber - 1) * 100);
        }
    }

    if (rng.savedGame === true && rng.savedGameFile != null) {
        deckManager.SetupFixedDecks(rng.savedGameFile);
    } else {
        deckManager.SetupDecks({
            hiveExpansionEnabled,
            cellarExpansionEnabled,
            includeAdvancedCards: advancedCardsEnabled,
            guaranteedActionCardNames: getCurrentGuaranteedCardNames(),
            currentSeed: rng.getCurrentSeed().toString(),
            campaignGameNumber: campaignState?.gameNumber ?? 1
        });
    }

    deckManager.ShowCurrentCards();
    beeDiceManager.setEnabled(hiveExpansionEnabled, roundManager.getCurrentRound());
    beeDiceManager.showRound(roundManager.getCurrentRound(), false);
}

function syncToggleVisualState(): void {
    const applyState = (toggle: HTMLButtonElement | null, isOn: boolean) => {
        if (toggle == null) {
            return;
        }

        toggle.classList.toggle('is-on', isOn);
        toggle.setAttribute('aria-checked', isOn.toString());
    };

    applyState(hiveToggleElement, hiveExpansionEnabled);
    applyState(cellarToggleElement, cellarExpansionEnabled);
    applyState(advancedCardsToggleElement, advancedCardsEnabled);
}

function getCurrentGuaranteedCardNames(): string[] {
    if (!cellarExpansionEnabled || campaignState == null) {
        return [];
    }

    return campaignState.currentGuaranteedNames.slice();
}

function handleGameFinished(): void {
    if (!cellarExpansionEnabled || campaignState == null) {
        syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
        return;
    }

    if (campaignState.gameNumber >= 3) {
        campaignState.phase = "complete";
        renderCellarCampaignOverlay();
        syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
        return;
    }

    campaignState.phase = "carryover-draft";
    campaignState.carryoverChosenNames = [];
    campaignState.carryoverAvailableNames = deckManager.getCurrentGameActionCardNames({ excludeStarterCards: true });
    campaignState.carryoverCurrentPickName = null;
    campaignState.draftFilter = "all";
    renderCellarCampaignOverlay();
    syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
}

function finalizeOpeningDraftPick(): void {
    if (campaignState == null || campaignState.openingCurrentPickName == null) {
        return;
    }

    if (!campaignState.openingChosenNames.includes(campaignState.openingCurrentPickName)) {
        campaignState.openingChosenNames.push(campaignState.openingCurrentPickName);
    }

    campaignState.openingCurrentPickName = null;
}

function finalizeCarryoverDraftPick(): void {
    if (campaignState == null || campaignState.carryoverCurrentPickName == null) {
        return;
    }

    if (!campaignState.carryoverChosenNames.includes(campaignState.carryoverCurrentPickName)) {
        campaignState.carryoverChosenNames.push(campaignState.carryoverCurrentPickName);
    }

    campaignState.carryoverAvailableNames = campaignState.carryoverAvailableNames.filter((cardName) => cardName !== campaignState.carryoverCurrentPickName);
    campaignState.carryoverCurrentPickName = null;
}

function generateCellarOffer(excludedNames: string[]): string[] {
    const excluded = new Set(excludedNames);
    const offer: string[] = [];
    const cellarSeed = `${rng.getCurrentSeed()}|cellar|game-${campaignState?.gameNumber ?? 1}|picked-${excludedNames.slice().sort().join(',')}`;

    for (const season of [1, 2, 3] as const) {
        const seasonCards = cellarOfferPool.filter((card) => card.season === season && !excluded.has(card.name));

        if (seasonCards.length === 0) {
            continue;
        }

        const selectedCard = seasonCards[getDeterministicCellarIndex(`${cellarSeed}|season-${season}`, seasonCards.length)];
        offer.push(selectedCard.name);
    }

    return offer;
}

function getDeterministicCellarIndex(seed: string, length: number): number {
    if (length <= 1) {
        return 0;
    }

    const hash = hashCellarSeed(seed);
    return Math.abs(hash) % length;
}

function hashCellarSeed(value: string): number {
    let hash = 0;

    for (let index = 0; index < value.length; index++) {
        hash = ((hash << 5) - hash) + value.charCodeAt(index);
        hash |= 0;
    }

    return hash;
}

function renderCellarCampaignOverlay(): void {
    if (cellarOverlayElement == null || campaignState == null || !cellarExpansionEnabled) {
        hideCellarCampaignOverlay();
        return;
    }

    if (campaignState.phase === "in-game") {
        hideCellarCampaignOverlay();
        return;
    }

    cellarOverlayElement.classList.toggle('is-opening-draft', campaignState.phase === "opening-draft");
    cellarOverlayElement.classList.toggle('is-carryover-draft', campaignState.phase === "carryover-draft");
    cellarOverlayElement.classList.toggle('is-between-games', campaignState.phase === "between-games");
    cellarOverlayElement.classList.toggle('is-complete', campaignState.phase === "complete");
    cellarOverlayElement.classList.add('show');
    cellarOverlayElement.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cellar-campaign-active');

    if (campaignState.phase === "opening-draft") {
        renderOpeningDraftOverlay();
        return;
    }

    if (campaignState.phase === "carryover-draft") {
        renderCarryoverDraftOverlay();
        return;
    }

    if (campaignState.phase === "between-games") {
        renderBetweenGamesOverlay();
        return;
    }

    renderCampaignCompleteOverlay();
}

function renderOpeningDraftOverlay(): void {
    if (campaignState == null) {
        return;
    }

    setCampaignOverlayText(
        "",
        "Choose a Cellar Card",
        ""
    );

    setSummaryHtml('Have each player choose a card for this first game.');

    renderFilters([]);
    const offerCards = campaignState.openingCurrentOfferNames
        .map((cardName) => getActionCardDefinition(cardName))
        .filter((card): card is ActionCardDefinition => card != null);

    renderCardChoices(offerCards, campaignState.openingCurrentPickName, new Set(), (card) => {
        if (campaignState == null) {
            return;
        }

        campaignState.openingCurrentPickName = campaignState.openingCurrentPickName === card.name ? null : card.name;
        renderCellarCampaignOverlay();
        syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
    });

    if (cellarNextPlayerButton != null) {
        cellarNextPlayerButton.textContent = 'Select';
    }

    if (cellarDoneButton != null) {
        cellarDoneButton.textContent = 'Done';
    }

    setCampaignButtons({
        nextPlayerVisible: true,
        doneVisible: true,
        nextGameVisible: false,
        closeVisible: false,
        nextPlayerDisabled: campaignState.openingCurrentPickName == null,
        doneDisabled: campaignState.openingCurrentPickName != null || campaignState.openingChosenNames.length === 0
    });
}

function renderCarryoverDraftOverlay(): void {
    if (campaignState == null) {
        return;
    }

    setCampaignOverlayText(
        "",
        "Draft Cards for Next Game",
        ""
    );

    setSummaryHtml(`
        All players should choose one card to carry over to the next game. Draft in order from most points to least.
    `);

    renderFilters([]);

    const unavailableCards = new Set(campaignState.carryoverChosenNames);
    const visibleCards = campaignState.carryoverAvailableNames
        .map((cardName) => getActionCardDefinition(cardName))
        .filter((card): card is ActionCardDefinition => card != null)
        .filter((card) => campaignState!.carryoverAvailableNames.includes(card.name));

    renderCardChoices(visibleCards, campaignState.carryoverCurrentPickName, unavailableCards, (card) => {
        if (campaignState == null) {
            return;
        }

        campaignState.carryoverCurrentPickName = campaignState.carryoverCurrentPickName === card.name ? null : card.name;
        renderCellarCampaignOverlay();
        syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
    });

    if (cellarNextPlayerButton != null) {
        cellarNextPlayerButton.textContent = 'Select';
    }

    if (cellarDoneButton != null) {
        cellarDoneButton.textContent = 'Done';
    }

    setCampaignButtons({
        nextPlayerVisible: true,
        doneVisible: true,
        nextGameVisible: false,
        closeVisible: false,
        nextPlayerDisabled: campaignState.carryoverCurrentPickName == null,
        doneDisabled: campaignState.carryoverCurrentPickName != null || campaignState.carryoverChosenNames.length === 0
    });
}

function renderBetweenGamesOverlay(): void {
    if (campaignState == null) {
        return;
    }

    setCampaignOverlayText(
        "",
        `Ready for Game ${campaignState.gameNumber}`,
        ""
    );

    setSummaryHtml(`Screenshot this QR code to use for Game ${campaignState.gameNumber}.`);
    renderFilters([]);
    renderCampaignQrCode(buildSeedShareUrl(rng.getCurrentSeed().toString()).toString());

    setCampaignButtons({
        nextPlayerVisible: false,
        doneVisible: false,
        nextGameVisible: true,
        closeVisible: false,
        nextPlayerDisabled: true,
        doneDisabled: true
    });
}

function renderCampaignCompleteOverlay(): void {
    if (campaignState == null) {
        return;
    }

    setCampaignOverlayText(
        "",
        "Game 3 is finished",
        ""
    );

    setSummaryHtml(`
        <strong>Final guaranteed cards used this game:</strong> ${formatCardList(campaignState.currentGuaranteedNames) || 'None'}
    `);
    renderFilters([]);
    renderCardChoices([], null, new Set(), () => undefined);

    setCampaignButtons({
        nextPlayerVisible: false,
        doneVisible: false,
        nextGameVisible: false,
        closeVisible: true,
        nextPlayerDisabled: true,
        doneDisabled: true
    });
}

function setCampaignOverlayText(eyebrow: string, title: string, description: string): void {
    if (cellarEyebrowElement != null) {
        cellarEyebrowElement.textContent = eyebrow;
    }

    if (cellarTitleElement != null) {
        cellarTitleElement.textContent = title;
    }

    if (cellarDescriptionElement != null) {
        cellarDescriptionElement.textContent = description;
    }
}

function setSummaryHtml(summaryHtml: string): void {
    if (cellarSummaryElement != null) {
        cellarSummaryElement.innerHTML = summaryHtml;
    }
}

function renderFilters(filters: { value: CampaignFilter; label: string }[]): void {
    if (cellarFiltersElement == null) {
        return;
    }

    cellarFiltersElement.innerHTML = '';
    cellarFiltersElement.style.display = filters.length === 0 ? 'none' : 'flex';

    for (const filter of filters) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cellar-filter-chip';
        button.textContent = filter.label;
        button.classList.toggle('is-active', campaignState?.draftFilter === filter.value);
        button.addEventListener('click', () => {
            if (campaignState == null) {
                return;
            }

            campaignState.draftFilter = filter.value;
            renderCellarCampaignOverlay();
            syncSeedShare(seedQrCodeElement, rng.getCurrentSeed().toString(), true);
        });
        cellarFiltersElement.appendChild(button);
    }
}

function renderCardChoices(cards: ActionCardDefinition[], selectedName: string | null, unavailableNames: Set<string>, onSelect: (card: ActionCardDefinition) => void): void {
    if (cellarCardGridElement == null) {
        return;
    }

    cellarCardGridElement.classList.remove('is-qr-mode');
    cellarCardGridElement.innerHTML = '';

    if (cards.length === 0) {
        return;
    }

    for (const card of cards) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cellar-choice-card';
        button.classList.toggle('is-selected', selectedName === card.name);
        button.classList.toggle('is-disabled', unavailableNames.has(card.name));
        button.disabled = unavailableNames.has(card.name);
        button.addEventListener('click', () => {
            if (button.disabled) {
                return;
            }

            onSelect(card);
        });

        const preview = document.createElement('div');
        preview.className = 'cellar-choice-preview';
        preview.style.backgroundImage = `url(${card.imageUrl})`;
        button.setAttribute('aria-label', card.name);
        button.title = card.name;

        button.appendChild(preview);
        cellarCardGridElement.appendChild(button);
    }
}

function renderCampaignQrCode(url: string): void {
    if (cellarCardGridElement == null) {
        return;
    }

    cellarCardGridElement.classList.add('is-qr-mode');
    cellarCardGridElement.innerHTML = '';

    const qrCanvas = document.createElement('canvas');
    qrCanvas.className = 'cellar-campaign-qr-code';
    qrCanvas.setAttribute('aria-label', 'QR code for the next game');

    const qrFrame = document.createElement('div');
    qrFrame.className = 'cellar-campaign-qr-frame';
    const qrSize = Math.max(132, Math.min(280, Math.floor(window.innerHeight * 0.28)));
    qrFrame.style.width = `${qrSize}px`;
    qrFrame.style.height = `${qrSize}px`;
    qrCanvas.style.width = `${qrSize}px`;
    qrCanvas.style.height = `${qrSize}px`;
    qrFrame.appendChild(qrCanvas);

    cellarCardGridElement.appendChild(qrFrame);

    toCanvas(qrCanvas, url, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: qrSize,
        color: {
            dark: '#0f1618',
            light: '#ffffff'
        }
    }).catch((error) => {
        console.error('Failed to render campaign QR code', error);
    });
}

function setCampaignButtons(options: { nextPlayerVisible: boolean; doneVisible: boolean; nextGameVisible: boolean; closeVisible: boolean; nextPlayerDisabled: boolean; doneDisabled: boolean }): void {
    if (cellarNextPlayerButton != null) {
        cellarNextPlayerButton.style.display = options.nextPlayerVisible ? '' : 'none';
        cellarNextPlayerButton.disabled = options.nextPlayerDisabled;
    }

    if (cellarDoneButton != null) {
        cellarDoneButton.style.display = options.doneVisible ? '' : 'none';
        cellarDoneButton.disabled = options.doneDisabled;
    }

    if (cellarNextGameButton != null) {
        cellarNextGameButton.style.display = options.nextGameVisible ? '' : 'none';
    }

    if (cellarCloseButton != null) {
        cellarCloseButton.style.display = options.closeVisible ? '' : 'none';
    }
}

function hideCellarCampaignOverlay(): void {
    if (cellarOverlayElement == null) {
        return;
    }

    cellarOverlayElement.classList.remove('show');
    cellarOverlayElement.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cellar-campaign-active');
}

function matchesCampaignFilter(card: ActionCardDefinition, filter: CampaignFilter): boolean {
    if (filter === "all") {
        return true;
    }

    if (filter === "season-1") {
        return card.season === 1;
    }

    if (filter === "season-2") {
        return card.season === 2;
    }

    if (filter === "season-3") {
        return card.season === 3;
    }

    return card.source === filter;
}

function formatSeason(card: ActionCardDefinition): string {
    return card.season == null ? 'Starter' : `Season ${card.season}`;
}

function formatCardList(cardNames: string[]): string {
    return cardNames.join(', ');
}

function syncTabletViewportHeight(): void {
    const root = document.documentElement;

    if (!tabletViewportQuery.matches) {
        root.classList.remove('has-tablet-visual-viewport');
        root.style.removeProperty('--tablet-visible-height');
        return;
    }

    const visibleHeight = Math.floor(window.visualViewport?.height ?? window.innerHeight);

    if (visibleHeight <= 0) {
        return;
    }

    root.classList.add('has-tablet-visual-viewport');
    root.style.setProperty('--tablet-visible-height', `${visibleHeight}px`);
}

function parseExpansionParam(searchParams: URLSearchParams): string[] {
    return (searchParams.get('expansions') ?? '')
        .split(',')
        .map(expansion => expansion.trim().toLocaleLowerCase())
        .filter(expansion => expansion.length > 0);
}

function parseAdvancedCardsParam(searchParams: URLSearchParams, legacyDefault: boolean): boolean {
    const cardMode = (searchParams.get('cards') ?? searchParams.get('advanced') ?? '').trim().toLocaleLowerCase();

    if (['advanced', 'true', '1', 'on'].includes(cardMode)) {
        return true;
    }

    if (['base', 'basic', 'not-advanced', 'not_advanced', 'false', '0', 'off'].includes(cardMode)) {
        return false;
    }

    return legacyDefault;
}

function buildSeedShareUrl(seed: string): URL {
    const seedUrl = new URL(window.location.href);
    seedUrl.pathname = '/';
    seedUrl.search = '';
    seedUrl.hash = '';
    seedUrl.searchParams.set('seed', seed);
    seedUrl.searchParams.set('cards', advancedCardsEnabled ? 'advanced' : 'base');

    const expansions: string[] = [];

    if (hiveExpansionEnabled) {
        expansions.push('hive');
    }

    if (cellarExpansionEnabled) {
        expansions.push('cellar');
    }

    if (expansions.length > 0) {
        seedUrl.searchParams.set('expansions', expansions.join(','));
    }

    if (cellarExpansionEnabled) {
        const campaignGameNumber = campaignState?.gameNumber ?? 1;
        const encodedGuaranteedCards = encodeSelectedActionCards(campaignState?.currentGuaranteedNames ?? []);

        seedUrl.searchParams.set('cg', campaignGameNumber.toString());

        if (encodedGuaranteedCards.length > 0) {
            seedUrl.searchParams.set('cc', encodedGuaranteedCards);
        }
    }

    return seedUrl;
}

function syncSeedShare(canvas: HTMLCanvasElement | null, seed: string, updateAddressBar: boolean): void {
    const seedUrl = buildSeedShareUrl(seed);
    updateSeedQrCode(canvas, seedUrl.toString());

    if (updateAddressBar) {
        window.history.replaceState(null, '', `${seedUrl.pathname}${seedUrl.search}`);
    }
}

function updateSeedQrCode(canvas: HTMLCanvasElement | null, seedUrl: string): void {
    if (canvas == null) {
        return;
    }

    canvas.title = seedUrl;

    void toCanvas(canvas, seedUrl, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 132,
        color: {
            dark: '#0f1618ff',
            light: '#ffffffff'
        }
    }).catch((error) => {
        console.error('Failed to render seed QR code', error);
    });
}
